import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './lib/cn';
import { PRESENCE_META, type PresenceState } from './lib/presence';

/**
 * The badge is a filled (or outlined) circle wearing a ring in the colour of
 * the surface behind it, so it reads as a cut-out on any background. The ring
 * is a `ring-*` utility, never a border — a border would eat into the circle
 * and leave no room for the glyph at list-row sizes.
 */
const presenceDot = cva(
  'inline-flex shrink-0 items-center justify-center rounded-pill ring-2 ring-card',
  {
    variants: {
      size: {
        xs: 'h-2.5 w-2.5', // 10px — 20/24px avatars, inline beside a name
        sm: 'h-3 w-3', //     12px — 32px avatars (chat list rows)
        md: 'h-3.5 w-3.5', // 14px — 40/48px avatars
        lg: 'h-4 w-4', //     16px — 56/80px avatars
        xl: 'h-5 w-5', //     20px — 96px profile / incoming-call avatar
      },
      hollow: {
        true: 'border-[1.5px]',
        false: 'text-white',
      },
    },
    defaultVariants: { size: 'md', hollow: false },
  },
);

type DotSize = NonNullable<VariantProps<typeof presenceDot>['size']>;

/**
 * Hollow states (the offline family) draw as an OUTLINED circle, so their
 * interior has to be painted in the colour of the surface behind the avatar —
 * otherwise the avatar shows through and the cross sits on a face.
 * `ringClassName` already names that surface, so the fill is derived from it
 * and the two can never drift apart. Written as literal class strings because
 * Tailwind only generates what it can read in the source.
 */
const SURFACE_FILL: Record<string, string | undefined> = {
  'ring-card': 'bg-card',
  'ring-sidebar': 'bg-sidebar',
  'ring-sidebar-hover': 'bg-sidebar-hover',
  'ring-sidebar-active': 'bg-sidebar-active',
  'ring-subtle': 'bg-subtle',
  'ring-popover': 'bg-popover',
  'ring-popup': 'bg-popup',
  'ring-topbar': 'bg-topbar',
  'ring-background': 'bg-background',
  'ring-muted': 'bg-muted',
  'ring-stage-tile': 'bg-stage-tile',
};

function surfaceFill(ringClassName: string | undefined): string {
  if (ringClassName === undefined) return 'bg-card';
  for (const token of ringClassName.split(/\s+/)) {
    const fill = SURFACE_FILL[token];
    if (fill !== undefined) return fill;
  }
  return 'bg-card';
}

interface GlyphSpec {
  size: number;
  strokeWidth: number;
}

/** Glyph sits at ~70% of the circle, with a heavier stroke as it shrinks. */
const GLYPH_SPEC: Record<DotSize, GlyphSpec> = {
  xs: { size: 7, strokeWidth: 4 },
  sm: { size: 9, strokeWidth: 4 },
  md: { size: 10, strokeWidth: 3.5 },
  lg: { size: 12, strokeWidth: 3 },
  xl: { size: 15, strokeWidth: 3 },
};

export interface PresenceDotProps extends Omit<VariantProps<typeof presenceDot>, 'hollow'> {
  state: PresenceState;
  /**
   * Accessible name and tooltip. Defaults to the label of `state`.
   *
   * The app vocabulary is richer than these 9 render states: Presenting and
   * Screen sharing both collapse onto `dnd`, In a call onto `busy`. The badge
   * that draws is then right, but its NAME is not — a colleague who is
   * presenting announced themselves as "Do not disturb", and one on a call as
   * "Busy". Pass the true state name wherever the dot is the only presence
   * affordance and the collapsed token would misname the person.
   */
  label?: string;
  /** Render the state glyph inside the dot. Busy states are solid by design. */
  withGlyph?: boolean;
  /** Pulse ring — use for "speaking" or an active incoming call only. */
  pulse?: boolean;
  /** Ring color must match the surface the dot sits on. Defaults to `ring-card`. */
  ringClassName?: string;
  /**
   * Drop the dot out of the accessibility tree. Use ONLY where the element
   * around it already names the state in text — a presence-picker row is
   * literally labelled "Do not disturb", so a labelled dot inside it makes a
   * screen reader say the state twice.
   */
  decorative?: boolean;
  /**
   * `false` drops the native `title` tooltip and keeps the accessible name -
   * for a caller that shows the sentence in a styled `Tooltip` of its own. The
   * browser's box pops up under the cursor, over whatever is below the dot.
   */
  nativeTooltip?: boolean;
  className?: string;
}

/** The status dot used on avatars, people rows and the top-bar profile. */
export function PresenceDot({
  state,
  label,
  size = 'md',
  withGlyph = true,
  pulse = false,
  ringClassName,
  decorative = false,
  nativeTooltip = true,
  className,
}: PresenceDotProps) {
  const meta = PRESENCE_META[state];
  const Glyph = meta.glyph;
  const glyph = GLYPH_SPEC[size ?? 'md'];
  const name = label ?? meta.label;

  return (
    <span
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': name, ...(nativeTooltip ? { title: name } : {}) })}
      className={cn(
        presenceDot({ size, hollow: meta.hollow }),
        meta.hollow && surfaceFill(ringClassName),
        meta.dotClass,
        pulse && 'animate-pulse-ring',
        ringClassName,
        className,
      )}
    >
      {withGlyph && Glyph ? (
        <Glyph
          size={glyph.size}
          strokeWidth={glyph.strokeWidth}
          className="text-current"
          aria-hidden
        />
      ) : null}
    </span>
  );
}
